import { use } from 'react';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';

import { CallPlanForm } from 'features/care-connect-settings/domain/models/call-plan-form';

type CallPlanInternalNumbers = {
  internalNumbers: ReturnType<typeof useFieldArray<CallPlanForm, 'internalNumbers'>>['fields'];
  internalNumbersEmptyErrorMessage: string | undefined;
  canAddInternalNumber: boolean;
  addInternalNumber: () => Promise<void>;
  removeInternalNumber: (index: number) => void;
};

const maximumInternalNumbers = 5;

const useCallPlanInternalNumbers = (callPlanForm: UseFormReturn<CallPlanForm>): CallPlanInternalNumbers => {
  const { t } = useTranslation('careConnectSettings', { keyPrefix: 'callPlanInternalNumbers' });
  const { showConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const isCallPlanEnabled = useWatch({ control: callPlanForm.control, name: 'isCallPlanEnabled' });
  const { fields: internalNumbers, replace } = useFieldArray({
    control: callPlanForm.control,
    name: 'internalNumbers',
    rules: {
      validate: (numbers) => !isCallPlanEnabled || numbers.length > 0 || t('validationRules.atLeastOneRequired'),
    },
  });
  const internalNumberValues = useWatch({ control: callPlanForm.control, name: 'internalNumbers' });
  const internalNumbersEmptyErrorMessage = callPlanForm.formState.errors.internalNumbers?.root?.message;
  const canAddInternalNumber = internalNumberValues.length < maximumInternalNumbers;

  const addInternalNumber = async (): Promise<void> => {
    replace([...internalNumberValues, { number: '', isUsedForOutGoingCalls: false }]);
    await callPlanForm.trigger('internalNumbers');
  };

  const removeInternalNumber = (index: number): void => {
    const removeInternalNumber = (): void =>
      replace(internalNumberValues.filter((_, internalNumberIndex) => internalNumberIndex !== index));
    const internalNumberValue = internalNumberValues[index]?.number;
    const internalNumberId = internalNumberValues[index]?.id;

    if (!internalNumberId && !internalNumberValue) {
      removeInternalNumber();
      return;
    }

    showConfirmationPopUp(
      removeInternalNumber,
      t('deleteConfirmationPopup.title'),
      t('deleteConfirmationPopup.body'),
      t('deleteConfirmationPopup.confirm'),
      t('deleteConfirmationPopup.cancel'),
    );
  };

  return {
    internalNumbers,
    internalNumbersEmptyErrorMessage,
    canAddInternalNumber,
    addInternalNumber,
    removeInternalNumber,
  };
};

export default useCallPlanInternalNumbers;
